import { BaseBannerData, SpecialBannerData } from '../banners'
import { GTM_MAIN_EVENT_NAME, GtmEvent, GtmEventTypes } from '.'

export interface BannerImpressionData extends BaseBannerData {}

export interface BannerClickData extends BaseBannerData {}

export interface SpecialBannerImpressionData extends SpecialBannerData {}

export interface SpecialBannerClickData extends SpecialBannerData {}

export interface BannerClickEvent extends GtmEvent {
  detail: {
    eventType: GtmEventTypes.BannerClick
    data: BannerClickData | SpecialBannerClickData
  }
}

export interface BannerImpressionEvent extends GtmEvent {
  detail: {
    eventType: GtmEventTypes.BannerImpression
    data: BannerImpressionData | SpecialBannerImpressionData
  }
}

export const isBannerClickEvent = (evt: GtmEvent): evt is BannerClickEvent =>
  evt.detail.eventType === GtmEventTypes.BannerClick

export const isBannerImpressionEvent = (
  evt: GtmEvent
): evt is BannerImpressionEvent =>
  evt.detail.eventType === GtmEventTypes.BannerImpression

export const gaMapping = {
  BackgroundBanner: 'Background banner',
  BannerFarm: 'Banner farm',
  Banners: 'banner',
  CampaignBanners: 'Campaign banners',
  Carousel: 'Slider banner',
  CmsCarouselWithImage: 'CMS Carousel with image',
  DailyDealBanner: 'Daily deal',
  FloatingBanner: 'Floating banner',
  HeaderBanner: 'Header banner',
  HpBanner: 'HP top banner',
  InfoBanner: 'Info banner',
  InspoBanner: 'Inspo banner',
  Pictogram: 'Pictogram',
  SectionBanner: 'Section banner',
  SlimBanner: 'Slim banner',
  TopIconsBanner: 'Top Icons Banner',
  UpperRightCornerBanner: 'Upper right corner',
  UspBanner: 'Header banner USP',
  MenuBannerFull: 'Menu banner full',
  MenuBannerHalf: 'Menu banner half',
  MenuBannerBrand: 'Menu banner brand',
}

export interface GtmBanner {
  bannerId: string
  creative: string
  id?: string
  name: string
  position: number
}

export const exportGtmBanner = (data: BaseBannerData): GtmBanner => {
  const {
    slidePosition,
    banner: { bannerId, bannerType, bannerSlides },
  } = data

  const googleAnalyticsTag = findBannerSliderById(data)

  const bannerName =
    bannerType === 'Banners' && bannerSlides.length
      ? bannerSlides.length === 1
        ? 'Full banner'
        : `1-${bannerSlides.length} ${gaMapping[bannerType]}`
      : gaMapping[bannerType]

  return {
    bannerId,
    creative: googleAnalyticsTag,
    name: bannerName,
    position: slidePosition || 1,
  }
}

const findBannerSliderById = (data: BaseBannerData) => {
  const { banner, slideId } = data

  if (banner?.bannerSlides && banner?.bannerSlides.length) {
    const bannerSlide = banner?.bannerSlides.find(
      (banner) => banner.slideId === slideId
    )
    return bannerSlide?.gaTag ?? banner.gaTag
  }

  return banner.gaTag
}

export const createBannerClickEvent = (
  data: BannerClickData | SpecialBannerClickData
): BannerClickEvent =>
  new CustomEvent(GTM_MAIN_EVENT_NAME, {
    bubbles: true,
    detail: { data, eventType: GtmEventTypes.BannerClick },
  })

export const createBannerImpressEvent = (
  data: BannerImpressionData | SpecialBannerImpressionData
): BannerImpressionEvent =>
  new CustomEvent(GTM_MAIN_EVENT_NAME, {
    bubbles: true,
    detail: { data, eventType: GtmEventTypes.BannerImpression },
  })
